import { useEffect, useState } from "react";
import { formatPackageItemsSummary } from "../../../components/productPackages/packageItems";
import MobileSearchBar from "../../ui/MobileSearchBar";
import MobileList from "../../ui/MobileList";
import MobileListItem from "../../ui/MobileListItem";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileError from "../../ui/MobileError";
import { mobileInput, mobilePrimaryBtn, mobileSecondaryBtn } from "../../theme";
import { useProductPackagesSearch } from "./useProductPackagesSearch";
import { MobileCreatePackageSheet, MobilePackageEditSheet } from "./MobilePackageSheets";

const formatCents = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value || 0) / 100);

const statusFromFilter = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};

export function packageTitle(productPackage) {
  return (
    productPackage?.internalName ||
    productPackage?.title ||
    productPackage?.slug ||
    "Pacote sem nome"
  );
}

export function packageSubtitle(productPackage) {
  const parts = [
    formatPackageItemsSummary(productPackage),
    formatCents(productPackage?.promoPriceCents),
    productPackage?.promoDeadlineBusinessDays
      ? `${productPackage.promoDeadlineBusinessDays} dias úteis`
      : "",
    productPackage?.isActive ? "Ativo" : "Inativo",
  ];

  return parts.filter(Boolean).join(" · ");
}

export default function MobileProductPackagesList({ user }) {
  const { packages, loading, error, search, refresh } = useProductPackagesSearch(user);
  const [q, setQ] = useState("");
  const [isActive, setIsActive] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    setHasSearched(true);
    search();
  }, [search]);

  const currentFilters = (nextQ = q, nextStatus = isActive) => ({
    q: nextQ,
    isActive: statusFromFilter(nextStatus),
  });

  const handleSearch = (rawQ) => {
    const value = typeof rawQ === "string" ? rawQ : q;
    setQ(value);
    setHasSearched(true);
    search(currentFilters(value));
  };

  const openEdit = (productPackage) => {
    setSelectedPackage(productPackage);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setSelectedPackage(null);
  };

  const handleSaved = () => {
    setCreateOpen(false);
    closeEdit();
    setHasSearched(true);
    refresh();
  };

  const searchLabel = q.trim() ? "Pesquisar" : "Pesquisar todos";

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className={mobilePrimaryBtn}
        >
          Criar pacote
        </button>
        <button
          type="button"
          onClick={() => {
            setHasSearched(true);
            refresh();
          }}
          className={mobileSecondaryBtn}
        >
          Atualizar
        </button>
      </div>

      <MobileSearchBar
        value={q}
        onChange={setQ}
        onSubmit={handleSearch}
        submitLabel={searchLabel}
        placeholder="Nome, título ou slug"
      />

      <div className="border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <label className="block text-sm font-medium text-laranjinha">
          Status
          <select
            aria-label="Status"
            value={isActive}
            onChange={(event) => setIsActive(event.target.value)}
            className={`mt-1 ${mobileInput}`}
          >
            <option value="">Todos</option>
            <option value="true">Ativos</option>
            <option value="false">Inativos</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div aria-label="Carregando pacotes" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : error ? (
        <MobileError
          message={error.message ?? "Erro ao buscar pacotes."}
          onRetry={refresh}
        />
      ) : hasSearched && packages.length === 0 ? (
        <MobileEmpty
          title="Nenhum pacote encontrado"
          message="Toque em Pesquisar para carregar a lista."
        />
      ) : packages.length > 0 ? (
        <MobileList>
          {packages.map((item) => (
            <MobileListItem
              key={item._id || item.id || item.slug}
              title={packageTitle(item)}
              subtitle={packageSubtitle(item)}
              onClick={() => openEdit(item)}
            />
          ))}
        </MobileList>
      ) : null}

      <MobileCreatePackageSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        user={user}
        onSaved={handleSaved}
      />

      <MobilePackageEditSheet
        open={editOpen}
        onClose={closeEdit}
        productPackage={selectedPackage}
        user={user}
        onSaved={handleSaved}
      />
    </div>
  );
}
